import LayoutHR from "layouts/layout-hr";
import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Offcanvas } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import MasterDataFormV2 from "../MasterDataFormV2";
import { AssetItemProvider, useAssetItem } from "./context/AssetItemContext";
import { AssetGroupProvider, useAssetGroup } from "./context/AssetGroupContext";
import { getDevelopmentBanner } from "utils/devNotice";
import { formatPrice } from "utils/utils";
import { useNavigate } from "react-router-dom";
import { deleteRequest, getRequest } from "services/api";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import { HR_ASSET_MANAGE_PERMISSIONS } from "utils/hrPermissionKeys";

const DUMMY_ASSET_ITEMS = [
  {
    id: "dummy-atv-001",
    name: "ATV 01",
    asset_code: "KBA-ATV-0001",
    asset_group_id: "dummy-group-atv",
    asset_group: { id: "dummy-group-atv", name: "ATV", code: "ATV" },
    asset_usage: "workshop_atv",
    plate_number: "DK 1234 ATV",
    model: "Yamaha Grizzly",
    year: 2024,
    atv_status: { id: "dummy-status-ready", name: "Siap Pakai" },
    price: 38000000,
    acquisition_date: "2024-01-15",
    depreciation_rate: 12.5,
    description: "Unit ATV untuk operasional Mekanik ATV.",
    status: "available",
  },
  {
    id: "dummy-atv-002",
    name: "ATV 02",
    asset_code: "KBA-ATV-0002",
    asset_group_id: "dummy-group-atv",
    asset_group: { id: "dummy-group-atv", name: "ATV", code: "ATV" },
    asset_usage: "workshop_atv",
    plate_number: "DK 5678 ATV",
    model: "Honda TRX",
    year: 2023,
    atv_status: { id: "dummy-status-service", name: "Perlu Service" },
    price: 36000000,
    acquisition_date: "2023-09-10",
    depreciation_rate: 12.5,
    description: "Unit ATV dengan jadwal service berkala.",
    status: "available",
  },
  {
    id: "dummy-laptop-001",
    name: "Laptop Admin FO",
    asset_code: "KBA-LPT-0001",
    asset_group_id: "dummy-group-laptop",
    asset_group: { id: "dummy-group-laptop", name: "Laptop", code: "LAPTOP" },
    asset_usage: "office",
    price: 9500000,
    acquisition_date: "2025-02-01",
    depreciation_rate: 25,
    description: "Laptop operasional Front Office.",
    status: "available",
  },
  {
    id: "dummy-tv-001",
    name: "TV LED Lobby",
    asset_code: "KBA-TV-0001",
    asset_group_id: "dummy-group-tv",
    asset_group: { id: "dummy-group-tv", name: "TV LED", code: "TV_LED" },
    asset_usage: "guest_area",
    price: 4200000,
    acquisition_date: "2024-06-20",
    depreciation_rate: 20,
    description: "TV LED untuk area lobby.",
    status: "available",
  },
  {
    id: "dummy-ht-001",
    name: "HT Guide 01",
    asset_code: "KBA-HT-0001",
    asset_group_id: "dummy-group-ht",
    asset_group: { id: "dummy-group-ht", name: "HT", code: "HT" },
    asset_usage: "operation",
    price: 850000,
    acquisition_date: "2024-11-12",
    depreciation_rate: 30,
    description: "HT untuk komunikasi team guide.",
    status: "available",
  },
  {
    id: "dummy-tablet-001",
    name: "Tablet Check-in",
    asset_code: "KBA-TAB-0001",
    asset_group_id: "dummy-group-tablet",
    asset_group: { id: "dummy-group-tablet", name: "Tablet", code: "TABLET" },
    asset_usage: "operation",
    price: 3200000,
    acquisition_date: "2025-04-05",
    depreciation_rate: 25,
    description: "Tablet untuk proses check-in dan operasional lapangan.",
    status: "available",
  },
  {
    id: "dummy-printer-001",
    name: "Printer Thermal POS",
    asset_code: "KBA-PRN-0001",
    asset_group_id: "dummy-group-printer",
    asset_group: { id: "dummy-group-printer", name: "Printer", code: "PRINTER" },
    asset_usage: "pos",
    price: 1250000,
    acquisition_date: "2025-01-18",
    depreciation_rate: 20,
    description: "Printer thermal untuk kasir dan print voucher.",
    status: "available",
  },
];

const getAssetPrice = (item) => Number(item?.price || item?.purchase_price || item?.acquisition_value || 0);

const getDepreciationRate = (item) => Number(item?.depreciation_rate || item?.depreciation_percentage || 0);

const getAcquisitionDate = (item) => item?.acquisition_date || item?.purchase_date || item?.created_at;

const getBackendCurrentBookValue = (item) =>
  item?.current_book_value ?? item?.currentBookValue ?? null;

const getBackendAccumulatedDepreciation = (item) =>
  item?.accumulated_depreciation ?? item?.accumulatedDepreciation ?? null;

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getAssetUsageLabel = (usage) => {
  const labels = {
    general: "General Asset",
    workshop_atv: "Mekanik ATV",
    office: "Office",
    operation: "Operation",
    guest_area: "Guest Area",
    pos: "POS",
    hr: "HR / Office",
  };
  return labels[usage] || usage || "General";
};

const getAssetGroupCode = (group) => group?.code || group?.asset_group_code || group?.group_code || "";

const isWorkshopAtv = (formData) => formData.asset_usage === "workshop_atv";

const getElapsedYears = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();
  if (Number.isNaN(date.getTime())) return 0;
  const now = new Date();
  return Math.max(0, now.getFullYear() - date.getFullYear() - (now < new Date(now.getFullYear(), date.getMonth(), date.getDate()) ? 1 : 0));
};

const getCurrentBookValue = (item) => {
  const backendValue = getBackendCurrentBookValue(item);
  if (backendValue !== null && backendValue !== undefined) return toNumber(backendValue);

  const price = getAssetPrice(item);
  const rate = getDepreciationRate(item) / 100;
  const elapsedYears = getElapsedYears(getAcquisitionDate(item));
  return Math.max(0, price - (price * rate * elapsedYears));
};

const getAccumulatedDepreciation = (item) => {
  const backendValue = getBackendAccumulatedDepreciation(item);
  if (backendValue !== null && backendValue !== undefined) return toNumber(backendValue);

  return Math.max(0, getAssetPrice(item) - getCurrentBookValue(item));
};

const buildDepreciationHistory = (item, yearsToShow = 8) => {
  const price = getAssetPrice(item);
  const rate = getDepreciationRate(item);
  const acquisitionDate = getAcquisitionDate(item);
  const startYear = acquisitionDate && !Number.isNaN(new Date(acquisitionDate).getTime())
    ? new Date(acquisitionDate).getFullYear()
    : new Date().getFullYear();
  const annualDepreciation = price * (rate / 100);

  return Array.from({ length: yearsToShow }, (_, index) => {
    const beginningValue = Math.max(0, price - (annualDepreciation * index));
    const depreciationValue = beginningValue > 0 ? Math.min(annualDepreciation, beginningValue) : 0;
    const bookValue = Math.max(0, beginningValue - depreciationValue);
    const accumulatedDepreciation = Math.min(price, annualDepreciation * (index + 1));
    return {
      year: startYear + index,
      beginningValue,
      rate,
      depreciationValue,
      accumulatedDepreciation,
      bookValue,
    };
  });
};

const normalizeDashboardSummary = (responseData) => {
  const data = responseData?.data || responseData || {};
  return {
    totalAssets: toNumber(data.total_assets),
    totalAcquisitionValue: toNumber(data.total_acquisition_value),
    totalBookValue: toNumber(data.total_book_value),
    totalDepreciationValue: toNumber(data.total_depreciation_value),
    yearlyDepreciationValue: toNumber(data.yearly_depreciation_value),
    availableAssets: toNumber(data.available_assets),
    unavailableAssets: toNumber(data.unavailable_assets),
    byGroup: data.by_group || [],
    byUsage: data.by_usage || [],
  };
};

const normalizeDepreciationHistory = (responseData, fallbackAsset) => {
  const data = responseData?.data || responseData || {};
  const rows = Array.isArray(data) ? data : data.rows || data.depreciation_history || [];
  return {
    asset: data.asset || fallbackAsset,
    rows: rows.map((row, index) => {
      const depreciationValue = toNumber(row.depreciation_value ?? row.depreciationValue);
      const accumulatedFallback = rows
        .slice(0, index + 1)
        .reduce((total, item) => total + toNumber(item.depreciation_value ?? item.depreciationValue), 0);
      return {
        year: row.year,
        beginningValue: toNumber(row.beginning_value ?? row.beginningValue),
        rate: toNumber(row.depreciation_rate ?? row.rate ?? getDepreciationRate(fallbackAsset)),
        depreciationValue,
        accumulatedDepreciation: toNumber(
          row.accumulated_depreciation ?? row.accumulatedDepreciation,
          accumulatedFallback
        ),
        bookValue: toNumber(row.book_value ?? row.bookValue),
      };
    }),
  };
};

function AssetItemContent() {
  const { assetItems, filterOptions, handleSearch, handleAssetFiltersChange, refetch, Pagination } =
    useAssetItem();
  const { assetGroups } = useAssetGroup();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [depreciationItem, setDepreciationItem] = useState(null);
  const [depreciationDetail, setDepreciationDetail] = useState(null);
  const [depreciationLoading, setDepreciationLoading] = useState(false);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [atvStatusOptions, setAtvStatusOptions] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState(filterOptions.search || "");
  const [localFilters, setLocalFilters] = useState({
    groupId: "",
    status: "",
    depreciation: "",
    usage: "",
  });

  const {
    // handleSuccess,
    handleError,
    // showCanvasHandler,
    handleSuccessToast,
    // handleLoadingThenSuccessToast,
  } = useCanvasHandler();

  useEffect(() => {
    const fetchAtvStatuses = async () => {
      try {
        const response = await getRequest("/atv-status?limit=100");
        if (response.success) {
          setAtvStatusOptions(
            (response.data || []).map((status) => ({
              value: status.id,
              label: status.name,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching ATV statuses:", error);
      }
    };

    fetchAtvStatuses();
  }, []);

  useEffect(() => {
    if (!depreciationItem) {
      setDepreciationDetail(null);
      return;
    }

    if (String(depreciationItem.id).startsWith("dummy-")) {
      setDepreciationDetail(null);
      return;
    }

    let cancelled = false;
    const fetchDepreciationHistory = async () => {
      setDepreciationLoading(true);
      try {
        let response = await getRequest(
          `/ahr/asset-items/${depreciationItem.id}/depreciation-history`
        );
        if (!response.success) {
          response = await getRequest(
            `/asset-items/${depreciationItem.id}/depreciation-history`
          );
        }
        if (!cancelled && response.success) {
          setDepreciationDetail(
            normalizeDepreciationHistory(response.data, depreciationItem)
          );
        }
      } catch (error) {
        if (!cancelled) setDepreciationDetail(null);
        console.error("Error fetching depreciation history:", error);
      } finally {
        if (!cancelled) setDepreciationLoading(false);
      }
    };

    fetchDepreciationHistory();
    return () => {
      cancelled = true;
    };
  }, [depreciationItem]);

  // Handle create new asset item
  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  // Handle edit asset item
  const handleEdit = (item) => {
    setShowModal(true);
    setEditingItem({
      ...item,
      asset_group_id: item.asset_group_id || item.asset_group?.id || item.assetGroup?.id || "",
      atv_status_id: item.atv_status_id || item.atv_status?.id || item.atvStatus?.id || "",
    });
  };

  // Handle delete asset item
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/asset-items/${id}`);
        if (response.success) {
          handleSuccessToast(true, "Asset item deleted successfully");
          refetch(); // Refresh the data
        } else {
          handleError("Failed to delete asset item");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Asset item saved successfully ");
  };

  // Handle form error
  const handleFormError = (error) => {
    handleError(error);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Form fields configuration
  const formFields = [
    {
      name: "name",
      label: "Asset Name",
      type: "text",
      required: true,
      placeholder: "Enter asset name",
    },
    {
      name: "asset_code",
      label: "Asset Code",
      type: "text",
      required: false,
      placeholder: "Optional, auto-generate if empty",
    },
    {
      name: "asset_group_id",
      label: "Asset Group",
      type: "select",
      required: true,
      options:
        assetGroups.data?.map((group) => ({
          value: group.id,
          label: group.name,
        })) || [],
      placeholder: "Select asset group",
    },
    {
      name: "asset_usage",
      label: "Asset Usage",
      type: "select",
      required: false,
      options: [
        { value: "general", label: "General Asset" },
        { value: "workshop_atv", label: "Mekanik ATV" },
        { value: "office", label: "Office" },
        { value: "operation", label: "Operation" },
        { value: "guest_area", label: "Guest Area" },
        { value: "pos", label: "POS" },
        { value: "hr", label: "HR / Office" },
      ],
      placeholder: "Select asset usage",
    },
    {
      name: "plate_number",
      label: "ATV Plate Number",
      type: "text",
      required: false,
      placeholder: "Required when usage is Mekanik ATV",
      showWhen: isWorkshopAtv,
    },
    {
      name: "model",
      label: "ATV Model",
      type: "text",
      required: false,
      placeholder: "Enter ATV model",
      showWhen: isWorkshopAtv,
    },
    {
      name: "year",
      label: "ATV Year",
      type: "number",
      required: false,
      placeholder: "Enter production year",
      showWhen: isWorkshopAtv,
    },
    {
      name: "chassis_number",
      label: "Chassis Number",
      type: "text",
      required: false,
      placeholder: "Enter chassis number",
      showWhen: isWorkshopAtv,
    },
    {
      name: "engine_number",
      label: "Engine Number",
      type: "text",
      required: false,
      placeholder: "Enter engine number",
      showWhen: isWorkshopAtv,
    },
    {
      name: "atv_validity",
      label: "ATV Validity",
      type: "number",
      required: false,
      placeholder: "Validity in years",
      showWhen: isWorkshopAtv,
    },
    {
      name: "last_service",
      label: "Last Service",
      type: "date",
      required: false,
      showWhen: isWorkshopAtv,
    },
    {
      name: "next_service",
      label: "Next Service",
      type: "date",
      required: false,
      showWhen: isWorkshopAtv,
    },
    {
      name: "atv_status_id",
      label: "ATV Status",
      type: "select",
      required: isWorkshopAtv,
      options: atvStatusOptions,
      placeholder: "Select ATV status",
      showWhen: isWorkshopAtv,
    },
    {
      name: "price",
      label: "Price",
      type: "number",
      required: true,
      placeholder: "Enter price",
    },
    {
      name: "acquisition_date",
      label: "Acquisition Date",
      type: "date",
      required: false,
      placeholder: "Select acquisition date",
    },
    {
      name: "depreciation_rate",
      label: "Depreciation Rate (%)",
      type: "number",
      required: false,
      placeholder: "Example: 10",
      min: "0",
      step: "0.01",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Enter asset description",
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: true,
      options: [
        { value: "available", label: "Available" },
        { value: "unavailable", label: "Unavailable" },
      ],
    },
  ];

  const displayAssetItems = useMemo(
    () =>
      assetItems.data?.length
        ? assetItems.data
        : DUMMY_ASSET_ITEMS,
    [assetItems.data]
  );

  const isDummyPreview = !assetItems.loading && !assetItems.data?.length;

  const displayAssetGroups = useMemo(() => {
    const apiGroups = assetGroups.data || [];
    if (apiGroups.length) return apiGroups;

    return DUMMY_ASSET_ITEMS.reduce((groups, item) => {
      if (!groups.some((group) => group.id === item.asset_group_id)) {
        groups.push(item.asset_group);
      }
      return groups;
    }, []);
  }, [assetGroups.data]);

  useEffect(() => {
    const selectedGroup = displayAssetGroups.find((group) => String(group.id) === String(localFilters.groupId));
    const params = new URLSearchParams();
    const groupCode = getAssetGroupCode(selectedGroup);
    if (groupCode) params.set("filter[asset_group_code]", groupCode);
    if (localFilters.usage) params.set("filter[asset_usage]", localFilters.usage);
    if (localFilters.status) params.set("filter[status]", localFilters.status);

    let cancelled = false;
    const fetchDashboardSummary = async () => {
      setDashboardLoading(true);
      try {
        const query = params.toString() ? `?${params.toString()}` : "";
        let response = await getRequest(`/ahr/asset-dashboard-summary${query}`);
        if (!response.success) {
          response = await getRequest(`/asset-dashboard-summary${query}`);
        }
        if (!cancelled && response.success) {
          setDashboardSummary(normalizeDashboardSummary(response.data));
        }
      } catch (error) {
        if (!cancelled) setDashboardSummary(null);
        console.error("Error fetching asset dashboard summary:", error);
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    };

    fetchDashboardSummary();
    return () => {
      cancelled = true;
    };
  }, [displayAssetGroups, localFilters.groupId, localFilters.status, localFilters.usage]);

  useEffect(() => {
    const selectedGroup = displayAssetGroups.find((group) => String(group.id) === String(localFilters.groupId));
    handleAssetFiltersChange({
      assetUsage: localFilters.usage,
      assetGroupCode: getAssetGroupCode(selectedGroup),
      assetStatus: localFilters.status,
    });
  }, [displayAssetGroups, handleAssetFiltersChange, localFilters.groupId, localFilters.status, localFilters.usage]);

  const filteredAssets = useMemo(() => {
    return displayAssetItems.filter((item) => {
      const groupId = item.asset_group_id || item.asset_group?.id || item.assetGroup?.id || "";
      const rate = getDepreciationRate(item);
      const usage = item.asset_usage || item.asset_type || "general";

      if (localFilters.groupId && String(groupId) !== String(localFilters.groupId)) return false;
      if (localFilters.status && item.status !== localFilters.status) return false;
      if (localFilters.depreciation === "with" && rate <= 0) return false;
      if (localFilters.depreciation === "without" && rate > 0) return false;
      if (localFilters.usage && usage !== localFilters.usage) return false;
      return true;
    });
  }, [displayAssetItems, localFilters]);

  const assetSummary = useMemo(() => {
    if (dashboardSummary) {
      return {
        totalAssets: dashboardSummary.totalAssets,
        totalValue: dashboardSummary.totalAcquisitionValue,
        bookValue: dashboardSummary.totalBookValue,
        totalDepreciation: dashboardSummary.totalDepreciationValue,
        yearlyDepreciation: dashboardSummary.yearlyDepreciationValue,
        available: dashboardSummary.availableAssets,
        unavailable: dashboardSummary.unavailableAssets,
      };
    }

    return displayAssetItems.reduce((summary, item) => {
      summary.totalAssets += 1;
      summary.totalValue += getAssetPrice(item);
      summary.bookValue += getCurrentBookValue(item);
      summary.totalDepreciation += getAccumulatedDepreciation(item);
      summary.yearlyDepreciation += getAssetPrice(item) * (getDepreciationRate(item) / 100);
      if (item.status === "available") summary.available += 1;
      if (item.status === "unavailable") summary.unavailable += 1;
      return summary;
    }, { totalAssets: 0, totalValue: 0, bookValue: 0, totalDepreciation: 0, yearlyDepreciation: 0, available: 0, unavailable: 0 });
  }, [dashboardSummary, displayAssetItems]);

  const clearLocalFilters = () => setLocalFilters({ groupId: "", status: "", depreciation: "", usage: "" });

  const transformAssetItemPayload = (data) => {
    const allowedFields = [
      "name",
      "asset_code",
      "asset_group_id",
      "asset_usage",
      "plate_number",
      "model",
      "year",
      "chassis_number",
      "engine_number",
      "atv_validity",
      "last_service",
      "next_service",
      "atv_status_id",
      "price",
      "acquisition_date",
      "depreciation_rate",
      "description",
      "status",
    ];
    const payload = allowedFields.reduce((result, fieldName) => {
      if (data[fieldName] !== undefined && data[fieldName] !== null) {
        result[fieldName] = data[fieldName];
      }
      return result;
    }, {});

    if (!payload.asset_usage) payload.asset_usage = "general";

    if (payload.asset_usage === "workshop_atv" && !payload.plate_number) {
      throw new Error("ATV Plate Number wajib diisi untuk asset usage Mekanik ATV");
    }

    if (payload.asset_usage === "workshop_atv" && !payload.atv_status_id) {
      throw new Error("ATV Status wajib diisi untuk asset usage Mekanik ATV");
    }

    if (payload.asset_usage !== "workshop_atv") {
      [
        "plate_number",
        "model",
        "year",
        "chassis_number",
        "engine_number",
        "atv_validity",
        "last_service",
        "next_service",
        "atv_status_id",
      ].forEach((fieldName) => delete payload[fieldName]);
    }

    return payload;
  };

  const depreciationAsset = depreciationDetail?.asset || depreciationItem;
  const depreciationRows = depreciationDetail?.rows?.length
    ? depreciationDetail.rows
    : depreciationItem
      ? buildDepreciationHistory(depreciationItem)
      : [];
  const dashboardByGroup = dashboardSummary?.byGroup || [];
  const dashboardByUsage = dashboardSummary?.byUsage || [];

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-fluid">
        {/* Page Header */}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-arrow-left"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <line x1="5" y1="12" x2="11" y2="18" />
                  <line x1="5" y1="12" x2="11" y2="6" />
                </svg>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Catatan Asset Kuber</h2>
              <div className="text-muted mt-1">
                Tracking asset, status, nilai buku, dan history penyusutan.
              </div>
              {isDummyPreview && (
                <span className="badge bg-warning-lt text-warning mt-2">
                  Dummy Preview - menunggu data API Asset Item
                </span>
              )}
            </div>
            <PermissionMiddleware allowedPermissions={HR_ASSET_MANAGE_PERMISSIONS}>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={handleCreate}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-plus"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Asset Item
                </button>
              </div>
            </PermissionMiddleware>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <AssetSummaryCard label="Total Asset" value={assetSummary.totalAssets} tone="primary" loading={dashboardLoading} />
          <AssetSummaryCard label="Nilai Perolehan" value={formatPrice(assetSummary.totalValue)} tone="success" loading={dashboardLoading} />
          <AssetSummaryCard label="Nilai Buku" value={formatPrice(assetSummary.bookValue)} tone="info" loading={dashboardLoading} />
          <AssetSummaryCard label="Akumulasi Penyusutan" value={formatPrice(assetSummary.totalDepreciation)} tone="danger" loading={dashboardLoading} />
          <AssetSummaryCard label="Penyusutan / Tahun" value={formatPrice(assetSummary.yearlyDepreciation)} tone="warning" loading={dashboardLoading} />
        </div>

        {(dashboardByGroup.length > 0 || dashboardByUsage.length > 0) && (
          <div className="row g-3 mb-3">
            {dashboardByGroup.length > 0 && (
              <div className="col-lg-7">
                <AssetBreakdownCard
                  title="Nilai Asset per Group"
                  rows={dashboardByGroup}
                  labelKey="asset_group_name"
                  fallbackLabelKey="asset_group_code"
                />
              </div>
            )}
            {dashboardByUsage.length > 0 && (
              <div className="col-lg-5">
                <AssetBreakdownCard
                  title="Nilai Asset per Usage"
                  rows={dashboardByUsage}
                  labelKey="asset_usage"
                  valueFormatter={getAssetUsageLabel}
                />
              </div>
            )}
          </div>
        )}

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-lg-3">
                <div className="input-icon">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search asset name, kode, lokasi..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={handleSearch}
                  />
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-search"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <circle cx="10" cy="10" r="7" />
                      <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="col-sm-6 col-lg-2">
                <select
                  className="form-select"
                  value={localFilters.groupId}
                  onChange={(event) => setLocalFilters((current) => ({ ...current, groupId: event.target.value }))}
                >
                  <option value="">All Groups</option>
                  {displayAssetGroups.map((group) => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-lg-2">
                <select
                  className="form-select"
                  value={localFilters.usage}
                  onChange={(event) => setLocalFilters((current) => ({ ...current, usage: event.target.value }))}
                >
                  <option value="">All Usage</option>
                  <option value="general">General Asset</option>
                  <option value="workshop_atv">Mekanik ATV</option>
                  <option value="office">Office</option>
                  <option value="operation">Operation</option>
                  <option value="guest_area">Guest Area</option>
                  <option value="pos">POS</option>
                  <option value="hr">HR / Office</option>
                </select>
              </div>
              <div className="col-sm-6 col-lg-2">
                <select
                  className="form-select"
                  value={localFilters.status}
                  onChange={(event) => setLocalFilters((current) => ({ ...current, status: event.target.value }))}
                >
                  <option value="">All Status</option>
                  <option value="available">Available</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>
              <div className="col-sm-6 col-lg-2">
                <select
                  className="form-select"
                  value={localFilters.depreciation}
                  onChange={(event) => setLocalFilters((current) => ({ ...current, depreciation: event.target.value }))}
                >
                  <option value="">All Depreciation</option>
                  <option value="with">With Rate</option>
                  <option value="without">Without Rate</option>
                </select>
              </div>
              <div className="col-sm-6 col-lg-2 d-flex gap-2">
                <button className="btn btn-outline-secondary w-100" onClick={clearLocalFilters}>
                  Reset
                </button>
                <button className="btn btn-outline-primary" onClick={refetch} title="Refresh">
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-header border-0 py-2 d-flex align-items-center justify-content-between">
            <div>
              <h3 className="card-title mb-0">Asset Register</h3>
              <div className="text-muted small">
                Menampilkan {filteredAssets.length} asset pada halaman ini.
                {isDummyPreview ? " Data berikut adalah contoh dummy." : ""}
              </div>
            </div>
            <div className="d-flex gap-2">
              <span className="badge bg-success-lt text-success">Available {assetSummary.available}</span>
              <span className="badge bg-danger-lt text-danger">Unavailable {assetSummary.unavailable}</span>
            </div>
          </div>
          <div className="table-responsive" style={{ maxHeight: "680px", overflow: "auto" }}>
              <table className="table table-vcenter table-hover table-sm mb-0">
                <thead className="sticky-top bg-white" style={{ zIndex: 2 }}>
                  <tr>
                    <th style={{ width: 60 }}>No</th>
                    <th style={{ width: 120 }}>Action</th>
                    <th style={{ minWidth: 220 }}>Asset</th>
                    <th style={{ minWidth: 160 }}>Group</th>
                    <th style={{ minWidth: 150 }}>Usage</th>
                    <th className="text-end" style={{ minWidth: 150 }}>Price</th>
	                    <th style={{ minWidth: 160 }}>Depreciation</th>
	                    <th className="text-end" style={{ minWidth: 150 }}>Book Value</th>
	                    <th className="text-end" style={{ minWidth: 170 }}>Accumulated</th>
	                    <th style={{ minWidth: 220 }}>Description</th>
	                    <th style={{ width: 120 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assetItems.loading ? (
                    <tr>
	                      <td colSpan="11" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading asset items...
                      </td>
                    </tr>
                  ) : filteredAssets.length > 0 ? (
                    filteredAssets.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          {(filterOptions.currentPage - 1) * 10 + index + 1}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="default"
                              size="sm"
                              id={`dropdown-${item.id}`}
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {!isDummyPreview && (
                                <PermissionMiddleware allowedPermissions={HR_ASSET_MANAGE_PERMISSIONS}>
                                  <Dropdown.Item onClick={() => handleEdit(item)}>
                                    Edit
                                  </Dropdown.Item>
                                </PermissionMiddleware>
                              )}
                              <Dropdown.Item onClick={() => setDepreciationItem(item)}>
                                Depreciation History
                              </Dropdown.Item>
                              {!isDummyPreview && (
                                <PermissionMiddleware allowedPermissions={HR_ASSET_MANAGE_PERMISSIONS}>
                                  <Dropdown.Item
                                    onClick={() => handleDelete(item.id)}
                                    className="text-danger"
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </PermissionMiddleware>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>
                          <div className="fw-bold">{item.name}</div>
                          <div className="text-muted small">{item.asset_code || item.code || item.location || "-"}</div>
                        </td>
	                        <td>{item.asset_group?.name || item.assetGroup?.name || "-"}</td>
                        <td>
                          <span className={`badge ${((item.asset_usage || item.asset_type) === "workshop_atv") ? "bg-blue-lt text-blue" : "bg-secondary-lt text-secondary"}`}>
                            {getAssetUsageLabel(item.asset_usage || item.asset_type)}
                          </span>
                          {(item.plate_number || item.related_atv_identifier || item.atv?.plate_number) && (
                            <div className="text-muted small">{item.plate_number || item.related_atv_identifier || item.atv?.plate_number}</div>
                          )}
                          {(item.model || item.year) && (
                            <div className="text-muted small">
                              {[item.model, item.year].filter(Boolean).join(" / ")}
                            </div>
                          )}
                          {(item.atv_status?.name || item.atvStatus?.name) && (
                            <div className="text-muted small">
                              Status: {item.atv_status?.name || item.atvStatus?.name}
                            </div>
                          )}
                        </td>
                        <td className="text-end fw-bold">{formatPrice(getAssetPrice(item))}</td>
                        <td>
                          <div className="fw-bold">{getDepreciationRate(item)}% / tahun</div>
                          <div className="text-muted small">
                            Mulai {getAcquisitionDate(item) ? new Date(getAcquisitionDate(item)).toLocaleDateString("id-ID") : "-"}
                          </div>
                        </td>
	                        <td className="fw-bold text-primary">{formatPrice(getCurrentBookValue(item))}</td>
	                        <td className="text-end text-danger fw-bold">{formatPrice(getAccumulatedDepreciation(item))}</td>
	                        <td>{item.description}</td>
                        <td>
                          <span
                            className={`badge text-white ${
                              item.status === "available"
                                ? "bg-success"
                                : item.status === "unavailable"
                                ? "bg-danger"
                                : "bg-warning"
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
	                      <td colSpan="11" className="text-center">
                        No asset items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
          </div>

          <div className="card-footer py-2">
            {assetItems.pagination && <Pagination />}
          </div>
        </div>

        {/* Modal for Create/Edit */}
        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem ? "Edit Asset Item" : "Create Asset Item"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/asset-items"
                    initialData={editingItem}
                    fields={formFields}
                    transformData={transformAssetItemPayload}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <Offcanvas
          show={!!depreciationItem}
          onHide={() => setDepreciationItem(null)}
          placement="end"
          style={{ width: "min(920px, 100vw)" }}
        >
          <Offcanvas.Header closeButton className="border-bottom">
            <div>
              <Offcanvas.Title>Depreciation History</Offcanvas.Title>
              <div className="text-muted small">{depreciationAsset?.name}</div>
            </div>
          </Offcanvas.Header>
          <Offcanvas.Body className="bg-light">
            {depreciationAsset && (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div className="card bg-primary-lt border-0">
                      <div className="card-body py-2">
                        <div className="text-muted small">Harga Asset</div>
                        <div className="h3 m-0">{formatPrice(getAssetPrice(depreciationAsset))}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-warning-lt border-0">
                      <div className="card-body py-2">
                        <div className="text-muted small">Rate Penyusutan</div>
                        <div className="h3 m-0">{getDepreciationRate(depreciationAsset)}%</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-success-lt border-0">
                      <div className="card-body py-2">
                        <div className="text-muted small">Nilai Buku Saat Ini</div>
                        <div className="h3 m-0">{formatPrice(getCurrentBookValue(depreciationAsset))}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card border-0 shadow-sm">
                  <div className="table-responsive">
                    <table className="table table-vcenter table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Tahun</th>
                          <th className="text-end">Nilai Awal</th>
                          <th className="text-end">Penyusutan</th>
                          <th className="text-end">Akumulasi</th>
                          <th className="text-end">Nilai Buku</th>
                        </tr>
                      </thead>
                      <tbody>
                        {depreciationLoading && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-4">
                              Loading depreciation history...
                            </td>
                          </tr>
                        )}
                        {!depreciationLoading && depreciationRows.map((row) => (
                          <tr key={row.year}>
                            <td className="fw-bold">{row.year}</td>
                            <td className="text-end">{formatPrice(row.beginningValue)}</td>
                            <td className="text-end text-danger">{formatPrice(row.depreciationValue)}</td>
                            <td className="text-end text-danger">{formatPrice(row.accumulatedDepreciation)}</td>
                            <td className="text-end fw-bold">{formatPrice(row.bookValue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="text-muted small mt-3">
                  History diambil dari backend bila tersedia. Jika endpoint belum tersedia, sistem memakai hitungan garis lurus dari harga asset, tanggal perolehan, dan persentase penyusutan.
                </div>
              </>
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    </LayoutHR>
  );
}

const AssetSummaryCard = ({ label, value, tone, loading }) => (
  <div className="col-sm-6 col-xl-3">
    <div className={`card bg-${tone}-lt border-0 h-100`}>
      <div className="card-body py-3">
        <div className="text-muted small">{label}</div>
        <div className="h3 m-0">{loading ? "..." : value}</div>
      </div>
    </div>
  </div>
);

const getBreakdownValue = (row, key) =>
  row?.[key] ?? row?.[key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())];

const AssetBreakdownCard = ({ title, rows, labelKey, fallbackLabelKey, valueFormatter }) => (
  <div className="card border-0 shadow-sm h-100">
    <div className="card-header border-0 py-2">
      <h3 className="card-title mb-0">{title}</h3>
    </div>
    <div className="table-responsive">
      <table className="table table-sm table-vcenter mb-0">
        <thead>
          <tr>
            <th>Group</th>
            <th className="text-end">Qty</th>
            <th className="text-end">Nilai Perolehan</th>
            <th className="text-end">Nilai Buku</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 6).map((row, index) => {
            const label =
              getBreakdownValue(row, labelKey) ||
              (fallbackLabelKey ? getBreakdownValue(row, fallbackLabelKey) : "") ||
              "-";
            return (
              <tr key={`${label}-${index}`}>
                <td className="fw-bold">{valueFormatter ? valueFormatter(label) : label}</td>
                <td className="text-end">{getBreakdownValue(row, "total_assets") || 0}</td>
                <td className="text-end">{formatPrice(getBreakdownValue(row, "total_acquisition_value") || 0)}</td>
                <td className="text-end">{formatPrice(getBreakdownValue(row, "total_book_value") || 0)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export default function AssetItemPage() {
  return (
    <AssetGroupProvider>
      <AssetItemProvider>
        <AssetItemContent />
      </AssetItemProvider>
    </AssetGroupProvider>
  );
}
